import { useEffect, useState, useCallback } from 'preact/hooks';
import type { AuthState, Category } from '@shared/types';
import { api } from './core/ApiClient';
import { LoginGate } from './components/LoginGate';
import { InitSetup } from './components/InitSetup';
import { Gnb } from './components/Gnb';
import { Footer } from './components/Footer';
import { CategorySidebar } from './components/CategorySidebar';
import { ContentView } from './components/ContentView';
import { SettingsPanel } from './components/SettingsPanel';
import { ManageView } from './components/ManageView';
import { RequestsView } from './components/RequestsView';
import { Toast } from './components/Toast';

type Section = 'blog' | 'manage' | 'requests';
type RequestKind = 'inquiry' | 'application';

/** 최초 URL의 ?post= 딥링크 파싱 (로그인 후 해당 글 상세로 진입) */
function readDeepLinkPostId(): number | null {
  const raw = new URLSearchParams(location.search).get('post');
  const id = raw ? Number(raw) : NaN;
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** 최초 URL의 ?requests=<kind>&rid=<id> 딥링크 파싱 (알림 클릭 → 신청·문의관리 해당 접수) */
function readDeepLinkRequests(): { kind: RequestKind; id: number | null } | null {
  const params = new URLSearchParams(location.search);
  const kindRaw = params.get('requests');
  if (kindRaw !== 'inquiry' && kindRaw !== 'application') return null;
  const idRaw = params.get('rid');
  const id = idRaw ? Number(idRaw) : NaN;
  return { kind: kindRaw, id: Number.isInteger(id) && id > 0 ? id : null };
}

export function App() {
  const [auth, setAuth] = useState<AuthState | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCat, setSelectedCat] = useState<number | null>(null);
  const [catNav, setCatNav] = useState(0); // 카테고리 클릭 시퀀스(같은 카테고리 재클릭도 목록 재표시 트리거)
  const [currentTitle, setCurrentTitle] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const initialReq = readDeepLinkRequests();
  const [section, setSection] = useState<Section>(initialReq ? 'requests' : 'blog');
  const [deepLinkPostId] = useState<number | null>(readDeepLinkPostId);
  const [deepLinkReq] = useState(initialReq);
  const [toast, setToast] = useState('');

  // 딥링크 파라미터는 소비 후 주소창에서 제거 (값은 state로 유지)
  useEffect(() => {
    if (deepLinkPostId || deepLinkReq) history.replaceState(null, '', location.pathname);
  }, [deepLinkPostId, deepLinkReq]);

  const refreshAuth = useCallback(async () => {
    const state = await api.me();
    setAuth(state);
  }, []);

  const loadCategories = useCallback(async () => {
    const list = await api.listCategories();
    setCategories(list);
    setSelectedCat((prev) => prev ?? (list.length > 0 ? list[0].id : null));
  }, []);

  useEffect(() => {
    api.setUnauthorizedHandler(() => setAuth((a) => (a ? { ...a, authenticated: false } : a)));
    void refreshAuth();
  }, [refreshAuth]);

  useEffect(() => {
    if (auth?.authenticated) void loadCategories();
  }, [auth?.authenticated, loadCategories]);

  if (auth === null) return <div class="empty">불러오는 중…</div>;

  if (!auth.authenticated) {
    if (!auth.initialized) {
      return <InitSetup defaultTitle={auth.siteTitle} onDone={refreshAuth} />;
    }
    return <LoginGate siteTitle={auth.siteTitle} onLoggedIn={refreshAuth} />;
  }

  const selectCategory = (id: number) => {
    setSelectedCat(id);
    setCatNav((n) => n + 1); // 활성 카테고리를 다시 눌러도 목록이 우측에 표시되도록
    setSidebarOpen(false);
  };

  return (
    <div class="app-shell">
      <Gnb
        siteTitle={auth.siteTitle}
        currentTitle={currentTitle}
        section={section}
        onSectionChange={setSection}
        onToggleSidebar={() => setSidebarOpen((v) => !v)}
        onOpenSettings={() => setSettingsOpen(true)}
        onToast={setToast}
        onLogout={async () => {
          await api.logout();
          await refreshAuth();
        }}
      />

      {section === 'blog' ? (
        <div class="body">
          <div
            class={`sidebar-backdrop ${sidebarOpen ? 'open' : ''}`}
            onClick={() => setSidebarOpen(false)}
          />
          <CategorySidebar
            categories={categories}
            selectedId={selectedCat}
            open={sidebarOpen}
            onSelect={selectCategory}
          />
          <main class="content">
            <ContentView
              categories={categories}
              categoryId={selectedCat}
              navSeq={catNav}
              onTitleChange={setCurrentTitle}
              initialPostId={deepLinkPostId}
            />
          </main>
        </div>
      ) : section === 'manage' ? (
        <main class="content content--full">
          <ManageView />
        </main>
      ) : (
        <main class="content content--full">
          <RequestsView initialKind={deepLinkReq?.kind} initialRequestId={deepLinkReq?.id ?? null} />
        </main>
      )}

      <Footer />

      {settingsOpen && (
        <SettingsPanel
          categories={categories}
          onClose={() => setSettingsOpen(false)}
          onCategoriesChanged={loadCategories}
        />
      )}

      {toast && <Toast message={toast} onDone={() => setToast('')} />}
    </div>
  );
}
